import React, { useState, useEffect } from 'react';
import { Indicator, Root } from '@radix-ui/react-checkbox';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { easing } from './lib/easing';
import Icon from './Icon';

const CheckboxIndicator = styled(Indicator).attrs({
  as: Icon,
  icon: 'check',
})`
  color: transparent;
`;

const Label = styled.label`
  font-weight: var(--semi-bold);
  color: var(--checkbox-color);
  cursor: pointer;
`;

const StarIcon = styled(Icon)`
  font-size: 16px;
`;

const CheckboxWrapper = styled(Root)`
  transition: ${easing('background-color', 'border-color', 'box-shadow')};
  align-items: center;
  background-color: white;
  border-radius: 4px;
  border: 2px solid var(--checkbox);
  box-shadow: none;
  color: white;
  display: flex;
  flex-shrink: 0;
  font-size: var(--xx-small);
  height: 16px;
  justify-content: center;
  position: relative;
  text-align: center;
  width: 16px;
  cursor: pointer;
  outline: none;

  &:hover {
    border-color: var(--checkbox-hover);
  }

  &:focus {
    border-color: var(--checkbox-hover);
    background: var(--checkbox-unselected-focus-bg);
    box-shadow: 0 0 0 5px var(--checkbox-unselected-focus-bg);
  }

  &[aria-checked='true'] {
    background: var(--checkbox-checked);
    border-color: var(--checkbox-checked);
    &:focus {
      background: var(--checkbox-checked-hover);
      box-shadow: 0 0 0 5px var(--checkbox-selected-focus-bg);
      border-color: var(--checkbox-checked-hover);
    }
    &:hover {
      background: var(--checkbox-checked-hover);
    }
    ${CheckboxIndicator} {
      color: white;
    }
  }

  &[aria-invalid='true'] {
    border-color: var(--danger-dark);
    color: var(--danger-dark);

    & + ${Label} {
      color: var(--danger-dark);
    }

    &:hover,
    &:focus-within {
      border-color: var(--checkbox-hover);
      background: transparent;
      box-shadow: none;
    }
  }

  &[aria-checked='true'][aria-invalid='true'] {
    background: transparent;
    box-shadow: none;
    border-color: var(--danger-dark);

    &:focus {
      background: transparent;
      box-shadow: none;
    }
    &:hover {
      border-color: var(--checkbox-hover);
      background: transparent;
    }

    ${CheckboxIndicator} {
      color: var(--danger-dark);
    }
  }

  &[disabled] {
    cursor: default;
    border-color: var(--checkbox-disabled);
    color: var(--checkbox-disabled);

    & + ${Label} {
      cursor: default;
      color: var(--checkbox-disabled);
    }
  }

  &[disabled][aria-checked='true'] {
    background: var(--checkbox-disabled);
    border-color: var(--checkbox-disabled);
    color: var(--checkbox-disabled);
  }
`;

const StarWrapper = styled(Root)`
  border: none;
  background: transparent;
  color: var(--checkbox-star-color);
  &[aria-checked='true'] {
    ${StarIcon} {
      color: var(--checkbox-star-checked-color);
    }
  }
`;

const Wrapper = styled.div`
  align-items: center;
  display: flex;

  position: relative;
  gap: 8px;
  width: fit-content;
`;

export const CheckboxGroup = styled.div`
  ${Wrapper} {
    min-height: 40px;
    padding: 12px 24px;

    &:hover {
      background-color: var(--checkbox-group-hover);
      border-radius: 6px;
    }
  }
`;

export const StarCheckbox = styled(
  ({
    children,
    label,
    onChange,
    id = `checkbox-id-${Math.random() * 100000}`,
    defaultChecked,
    checked,
    className,
    ...props
  }) => {
    const [isChecked, setChecked] = useState(defaultChecked || checked);

    const handleClick = () => {
      setChecked(currentChecked => {
        onChange?.(!currentChecked);
        return !currentChecked;
      });
    };

    useEffect(() => {
      setChecked(defaultChecked || checked);
    }, [defaultChecked, checked]);
    return (
      <Wrapper className={className}>
        <StarWrapper
          id={id}
          checked={isChecked}
          {...props}
          onClick={handleClick}
        >
          <StarIcon
            $checked={isChecked}
            icon={isChecked ? 'star' : ['far', 'star']}
          />
        </StarWrapper>
        {(children || label) && <Label htmlFor={id}>{children || label}</Label>}
      </Wrapper>
    );
  }
)``;

StarCheckbox.propTypes = {
  children: PropTypes.node,
  label: PropTypes.string,
  id: PropTypes.string,
  onChange: PropTypes.func,
  checked: PropTypes.bool,
  defaultChecked: PropTypes.bool,
  className: PropTypes.string,
};

StarCheckbox.displayName = 'StarCheckbox';

const Checkbox = React.forwardRef(
  (
    {
      children,
      label,
      onChange,
      id = `checkbox-id-${Math.random() * 100000}`,
      error,
      className,
      title,
      ...props
    },
    ref
  ) => {
    return (
      <Wrapper className={className} title={title}>
        <CheckboxWrapper
          id={id}
          onCheckedChange={onChange}
          aria-invalid={error}
          ref={ref}
          {...props}
        >
          <CheckboxIndicator />
        </CheckboxWrapper>
        {(children || label) && <Label htmlFor={id}>{children || label}</Label>}
      </Wrapper>
    );
  }
);

Checkbox.propTypes = {
  children: PropTypes.node,
  label: PropTypes.string,
  id: PropTypes.string,
  onChange: PropTypes.func,
  error: PropTypes.bool,
  className: PropTypes.string,
  title: PropTypes.string,
};

Checkbox.displayName = 'Checkbox';

export default styled(Checkbox)``;
